import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Flex, IconButton, Text, Tooltip, useDisclosure, useToast } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { FaExclamationCircle } from "react-icons/fa";
import { showErrorToast, translateSubElementString } from "../../../../../../../utils";
import { hideAppSpinner } from "../../../../../../../context/Store/slices/appSpinnerSlice";
import CategoryPreviewWrapper
  from "../../../../../../Reports/EmissionsReports/components/ScopeThree/components/categories/CategoryPreviewWrapper";
import { NumberField } from "../../../../../../../components/NumberField";
import { DataTable } from "../../../../../../../components/DataTable";
import {
  ReviewDataModal
} from "../../../../../../Reports/EmissionsReports/components/ScopeThree/components/ReviewDataModal";
import * as React from "react";
import {
  useGetUpstreamTransportationAndDistributionPreviewData
} from "../../../../../../../hooks/query/supplier-questionnaire/tabs/categories/upstream-transportation-and-distribution";

function CategoryFourConsumptionBasedPreview({ currentElement }) {
  const labelKey = "transportationAndDistribution";
  const { t } = useTranslation("translation", {
    keyPrefix: `reportsPage.scopeThree.inputs.${labelKey}`
  });
  const questionnaireKeyPrefix = `organizationQuestionnaire.scope3.inputs.${labelKey}`;
  const { t: questionnaireTranslation } = useTranslation("translation", {
    keyPrefix: questionnaireKeyPrefix
  });
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    isOpen: isReviewModalOpen,
    onOpen: onReviewModalOpen,
    onClose: onReviewModalClose
  } = useDisclosure();

  const { consumptionBasedData: { preview } } = currentElement;

  const [dataList, setDataList] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [reviewData, setReviewData] = useState(null);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    // Items are already pre-sorted by the backend by "typeOfTransport", "subtypeOfTransport" and "subtypeTwo"
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0
  });

  const {
    totalPages,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...newValObject
      };
    });
  };

  const columns = useMemo(() => {
      const handleReviewData = (attribute) => {
        onReviewModalOpen();
        setSelectedAttribute(attribute);
        if (attribute && preview?.categories?.upstreamTransportationDistribution) {
          setReviewData(preview?.categories?.upstreamTransportationDistribution[`${attribute}Items`].map(el => {
            return {
              ...el,
              [attribute]: questionnaireTranslation(`${attribute}.options.${el[attribute].code}`, {
                defaultValue: el[attribute] ?? ""
              })
            };
          }));
        }
      };

      return [
        {
          header: <Flex alignItems="center">
            {t("gridColumns.typeOfTransport")}
            <Tooltip
              label={t("reviewData", { attribute: t("gridColumns.typeOfTransport") })}
              whiteSpace={"pre-line"}
              hasArrow
            >
              <IconButton
                aria-label="upstreamTransportationAndDistributionInfo"
                type="button"
                icon={<FaExclamationCircle size="20" />}
                flexShrink="0"
                size="sm"
                opacity="0.25"
                tabIndex={-1}
                onClick={() => {
                  handleReviewData("typeOfTransport");
                }}
              />
            </Tooltip>
          </Flex>,
          accessorKey: "typeOfTransport",
          size: 230
        },
        {
          header: t("gridColumns.subtypeOfTransport"),
          accessorKey: "subtypeOfTransport",
          size: 220,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: t("gridColumns.subtypeTwo"),
          accessorKey: "subtypeTwo",
          size: 220,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: t("gridColumns.unit"),
          accessorKey: "unit",
          size: 100,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: t("gridColumns.value"),
          accessorKey: "value",
          size: 100,
          customStyles: {
            verticalAlign: "middle"
          }
        },
        {
          header: translateSubElementString(`reportsPage.scopeThree.inputs.${labelKey}.gridColumns.emissions`),
          accessorKey: "emissions",
          size: 150,
          customStyles: {
            verticalAlign: "middle"
          }
        }
      ];
    },
    [t, onReviewModalOpen, questionnaireTranslation, preview?.categories?.upstreamTransportationDistribution]
  );

  const handleReviewModalClose = () => {
    setSelectedAttribute(null);
    setReviewData(null);
    onReviewModalClose();
  };

  const {
    isLoading,
    isFetching
  } = useGetUpstreamTransportationAndDistributionPreviewData(
    preview?.id,
    { page: queryPageIndex, size: queryPageSize, sort: queryPageSortBy },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const dataItems = pageContent.map((item) => {
          return {
            id: item.id,
            typeOfTransport: questionnaireTranslation(`typeOfTransport.options.${item.typeOfTransport?.code ?? ""}`, {
              defaultValue: item.typeOfTransport?.name ?? ""
            }),
            subtypeOfTransport: questionnaireTranslation(`subtypeOfTransport.options.${item.subtypeOfTransport?.code ?? ""}`, {
              defaultValue: item.subtypeOfTransport?.name ?? ""
            }),
            subtypeTwo: questionnaireTranslation(`subtypeTwo.options.${item.subtypeTwo?.code ?? ""}`, {
              defaultValue: item.subtypeTwo?.name ?? ""
            }),
            unit: questionnaireTranslation(`unit.options.${item.unit?.code ?? ""}`, {
              defaultValue: item.unit?.name ?? ""
            }),
            value: Number(item.value)?.toLocaleString("en-GB", { maximumFractionDigits: 4 }),
            emissions: Number(item.co2Emissions)?.toLocaleString("en-GB", { maximumFractionDigits: 4 })
          };
        });

        setDataList(dataItems);
        if (pageContent.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && queryPageIndex > 0) {
          // Case when page size is changed to larger and user has to be redirected to the previous page
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0,
            queryPageIndex: queryPageIndex - 1
          });
        } else {
          manageDataTableState({
            totalElements: page?.totalElements ?? 0,
            totalPages: page?.totalPages ?? 0
          });
        }
      },
      onError: err => showErrorToast(toast, err.message),
      onSettled: () => {
        dispatch(hideAppSpinner());
      },
      cacheTime: 0,
      enabled: preview?.id !== null && typeof preview?.id !== "undefined"
    }
  );

  return (
    <CategoryPreviewWrapper>
      <Flex marginBlockEnd={4}>
        <Text size="sm" fontWeight="bold">
          {translateSubElementString(
            `reportsPage.scopeThree.inputs.mainTotalEmissions`
          )}
        </Text>
        <NumberField
          value={preview?.categories?.upstreamTransportationDistribution?.totalCo2Emissions ?? "n/a"}
          marginStart="2" />
      </Flex>
      <DataTable
        data={dataList}
        columns={columns}
        isLoading={isLoading || isFetching}
        containerHeight={dataList && dataList.length > 0 ? "370px" : null}
        marginBlockEnd={8}
        serverSidePagination={true}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
      />
      {
        isReviewModalOpen && selectedAttribute !== null && reviewData !== null && (
          <ReviewDataModal
            isOpen={isReviewModalOpen}
            onClose={handleReviewModalClose}
            dataAttribute={selectedAttribute}
            gridHeader={t(`gridColumns.${selectedAttribute}`)}
            data={reviewData}
          />
        )
      }
    </CategoryPreviewWrapper>
  );
}

export default CategoryFourConsumptionBasedPreview;